import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { fetchCompetitionContent, fetchCompetitionGroup, fetchCompetitionGroups, fetchCompetitionReportShareId, fetchCompetitors, fetchSharedCompetitionGroup } from "services/api/competition-analysis";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { CompetitionAnalysisState, CompetitionContentFilters, CompetitionGroup,  CompetitionPost,  Competitor,  CreatorFollowerTier } from "types/competition-analysis";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

export const fetchCompetitionAnalysisGroupsThunk = createAsyncThunk<{ items: CompetitionGroup[], count: number }, { force?: boolean }, { rejectValue: string }>(
    'competition-analysis/groups',
    async ({ force = false}, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_COMPETITION_GROUPS.staleTime;
            const state = getState() as { auth: AuthState, competitionAnalysis: CompetitionAnalysisState };
            if (!force && isFresh(state.competitionAnalysis.groups.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            if (!agencyId) throw new Error("No agency selected!");
            const { items, count } = await fetchCompetitionGroups(agencyId);
            return { items, count };
        } catch (error) {
            return rejectWithValue('Failed to fetch groups!');
        }
    }
)

export const fetchCompeitionGroupThunk = createAsyncThunk<{ creator_follower_tiers: CreatorFollowerTier[], competition_group: CompetitionGroup, competitionGroupId: string }, { competitionGroupId: string, force?: boolean }, { rejectValue: string }>(
    'competition-analysis/group',
    async ({ competitionGroupId, force = false}, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_COMPETITION_GROUPS.staleTime;
            const state = getState() as { auth: AuthState, competitionAnalysis: CompetitionAnalysisState };
            if (!force && isFresh(state.competitionAnalysis.group[competitionGroupId!]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            if (!agencyId) throw new Error("No agency selected!");
            const { creator_follower_tiers, competition_group } = await fetchCompetitionGroup(agencyId, competitionGroupId);
            return { creator_follower_tiers, competition_group, competitionGroupId };
        } catch (error) {
            return rejectWithValue('Failed to fetch groups!');
        }
    }
)


export const fetchSharedCompeitionGroupThunk = createAsyncThunk<{ creator_follower_tiers: CreatorFollowerTier[], competition_group: CompetitionGroup, competitionGroupShareId: string }, { competitionGroupShareId: string, force?: boolean }, { rejectValue: string }>(
    'competition-analysis/shared-group',
    async ({ competitionGroupShareId, force = false}, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_COMPETITION_GROUPS.staleTime;
            const state = getState() as { competitionAnalysis: CompetitionAnalysisState };
            if (!force && isFresh(state.competitionAnalysis.shared_group[competitionGroupShareId!]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const { creator_follower_tiers, competition_group } = await fetchSharedCompetitionGroup( competitionGroupShareId);
            return { creator_follower_tiers, competition_group, competitionGroupShareId };
        } catch (error) {
            return rejectWithValue('Failed to fetch groups!');
        }
    }
)

export const fetchCompetitorsThunk = createAsyncThunk<{ items: Competitor[], count: number }, { force?: boolean }, { rejectValue: string }>(
    'competition-analysis/competitors',
    async ({ force = false}, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_COMPETITORS.staleTime;
            const state = getState() as { auth: AuthState, competitionAnalysis: CompetitionAnalysisState };
            if (!force && isFresh(state.competitionAnalysis.competitors.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            if (!agencyId) throw new Error("No agency selected!");
            const { items, count } = await fetchCompetitors(agencyId);
            return { items, count };
        } catch (error) {
            return rejectWithValue('Failed to fetch groups!');
        }
    }
)

export const fetchCompetitionReportShareIdThunk = createAsyncThunk<{ uuid: string, competitionGroupId: string }, { competitionGroupId: string, force?: boolean }, { rejectValue: string }>(
    'competition-analysis/report/share',
    async ({ competitionGroupId, force = false}, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_COMPETITION_REPORT_SHARE_ID.staleTime;
            const state = getState() as { auth: AuthState, competitionAnalysis: CompetitionAnalysisState };
            if (!force && isFresh(state.competitionAnalysis.shares.data?.[competitionGroupId]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            if (!agencyId) throw new Error("No agency selected!");
            const { uuid } = await fetchCompetitionReportShareId(agencyId, competitionGroupId);
            return { uuid, competitionGroupId };
        } catch (error) {
            return rejectWithValue('Failed to fetch share id!');
        }
    }
)

export const fetchCompetitionContentThunk = createAsyncThunk<{items: CompetitionPost[], filters: CompetitionContentFilters, filterId: string, count: number}, {filters: CompetitionContentFilters, force?: boolean}, {rejectValue: string}>(
    'competition-analysis/content',
    async ({filters, force=false}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_COMPETITION_CONTENT.staleTime;
            const filterId = generateUniqueKey(filters, ['page']);
            const state = getState() as { auth: AuthState, competitionAnalysis: CompetitionAnalysisState };
            if (filters.page === 1 && !force && isFresh(state.competitionAnalysis.content?.filtered_lists?.[filterId]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const {count, items} = await fetchCompetitionContent(agencyId, filters);
            return { items, filters, filterId, count };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'failed to fetch social account tracked posts'));
        }
    }
);